import * as zod from "zod";
import { UserStatusType } from "@aws-sdk/client-cognito-identity-provider";
import { hosts } from "../../routing/hosts";

export const loginMethod = zod.enum(["EMAIL_PASSWORD", "GOOGLE", "UPSTREAM"]);
export type LoginMethod = zod.infer<typeof loginMethod>;

export const upstreamProviderSource = zod.enum([
  "CUSTOM",
  "GOOGLE",
  "MICROSOFT",
]);
export type UpstreamProviderSource = zod.infer<typeof upstreamProviderSource>;

export type CognitoUserAttributes = {
  user_name: string;
  sub: string;
  email: string;
  email_verified: boolean;
  picture?: string;
  given_name: string;
  family_name: string;
  phone_number?: string;
  ["custom:company_name"]?: string;
  ["custom:pending_email"]?: string;
  ["custom:mfa_totp"]?: string;
  ["custom:mfa_type"]?: string;
  ["custom:picture_federated"]?: string;
  ["custom:metadata"]?: IUserMetaData;
  ["custom:external_userids"]?: string;
  identities?: {
    userId: string;
    providerName: string;
    providerType: string;
    issuer: string | null;
    primary: boolean;
    dateCreated: number;
  }[];
  enabled?: boolean;
  lastModifiedDate?: Date;
  user_status?: UserStatusType;
};

export interface IUserMetaData {
  current_picture_federated?: string;
  migration_data?: {
    appId: string;
    status: "MIGRATED" | "NA";
    uniqueKey: string;
    access: string;
    migratedOn: Date;
  }[];
}

export interface IUserSignInData {
  email: string;
  currentPassword: string;
}

export interface IUserSignUpData {
  email: string;
  newPassword: string;
  given_name: string;
  family_name: string;
}

export interface IConfirmEmailData {
  email: string;
  verificationCode: string;
}

export interface IConfirmForgotPasswordData extends IConfirmEmailData {
  password: string;
}

export const FederatedAuthParamsSchema = zod.object({
  code: zod.string().min(1),
  state: zod.string().min(1),
});
export type FederatedAuthParams = zod.infer<typeof FederatedAuthParamsSchema>;

export const ForwardingParamsSchema = zod
  .object({
    src: zod.string().min(1),
    follow: zod.string().url().optional(),
  })
  /** @todo - we should probably just rename src on the stone side */
  .transform(({ src, ...rest }) => ({
    appId: src,
    ...rest,
  }));
export type ForwardingParams = zod.infer<typeof ForwardingParamsSchema>;

export const AuthContextSchema = zod.object({
  appId: zod.string().min(1).optional(),
  workspace: zod.string().min(1).optional(),
  orgName: zod.string().min(1).optional(),
  loginMethods: zod.array(loginMethod).optional(),
  upstreamProviders: zod
    .array(
      zod.object({
        id: zod.string().min(1),
        source: upstreamProviderSource,
        customLabel: zod.string().optional(),
      }),
    )
    .optional(),
  assets: zod
    .object({
      logo: zod
        .string()
        .optional()
        .transform((logo) =>
          logo && !logo.startsWith("http") ? `${hosts.assets}/${logo}` : logo,
        ),
      background: zod
        .string()
        .optional()
        .transform((background) =>
          background && !background.startsWith("http")
            ? `${hosts.assets}/${background}`
            : background,
        ),
    })
    .optional(),
  follow: zod.string().url().optional(),
  authenticated: zod.boolean().optional(),
});
export type AuthContext = zod.infer<typeof AuthContextSchema>;

export const AuthContextCookieSchema = AuthContextSchema.pick({
  appId: true,
  workspace: true,
  follow: true,
});
export type AuthContextCookie = zod.infer<typeof AuthContextCookieSchema>;

export const MICROSOFT_PROVIDER_ID = "microsoft-multitenant";
export const SONGTRADR_INTERNAL_AUTH_PROVIDER_ID = "songtradr-internal-auth";

export const UpstreamProviderSchema = zod.object({
  providerId: zod.union([
    zod.string().uuid(),
    zod.literal(MICROSOFT_PROVIDER_ID),
    zod.literal(SONGTRADR_INTERNAL_AUTH_PROVIDER_ID),
  ]),
  providerName: zod.string().min(3),
  providerSource: upstreamProviderSource,
  displayProviderName: zod.boolean().default(false),
  clientId: zod.string().min(7),
  clientSecret: zod.string().min(7),
  issuerUrl: zod.string().url(),
  mappings: zod
    .object({
      email: zod.string().default("email"),
      given_name: zod.string().default("given_name"),
      family_name: zod.string().default("family_name"),
      upstreamId: zod.string().default("upn"),
    })
    .default({}),
  metadata: zod.record(zod.string()).optional(),
  clientSecretUpdatedAt: zod.string().datetime().optional(),
});

export type UpstreamProviderModel = zod.infer<typeof UpstreamProviderSchema>;
export type UpstreamProviderClaimMapping =
  keyof UpstreamProviderModel["mappings"];
